import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { Send, CheckCircle, MessageSquare, Mail, User } from 'lucide-react'

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    console.log('Form data:', data)
    setIsLoading(false)
    setIsSubmitted(true)
    reset()
    setTimeout(() => setIsSubmitted(false), 5000)
  }

  const sendWhatsApp = () => {
    const form = document.getElementById('contactForm')
    const name = form.name.value
    const title = form.title.value
    const details = form.details.value
    const phone = '8801521721890'
    const message = `Hello, I am ${name}.%0AProject: ${title}%0ADetails: ${details}`
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank')
  }

  return (
    <section id="contact" className="py-20 px-6 bg-slate-900/70">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              Let's Build Something Great
            </span>
          </h2>
          <p className="text-white/70 text-lg">
            Tell us about your project. We'll reply on WhatsApp within 24 hours.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="glass-card p-8"
        >
          <form id="contactForm" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <User size={16} className="text-blue-400" />
                Your Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className={`w-full p-3 rounded-lg bg-white/5 border ${
                  errors.name ? 'border-red-500' : 'border-white/10'
                } placeholder:text-white/50 focus:outline-none focus:border-blue-500 transition-colors`}
                placeholder="John Doe"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Mail size={16} className="text-blue-400" />
                Email (Optional)
              </label>
              <input
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                type="email"
                className={`w-full p-3 rounded-lg bg-white/5 border ${
                  errors.email ? 'border-red-500' : 'border-white/10'
                } placeholder:text-white/50 focus:outline-none focus:border-blue-500 transition-colors`}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Project Title */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <MessageSquare size={16} className="text-blue-400" />
                Project Title *
              </label>
              <input
                {...register('title', { required: 'Project title is required' })}
                className={`w-full p-3 rounded-lg bg-white/5 border ${
                  errors.title ? 'border-red-500' : 'border-white/10'
                } placeholder:text-white/50 focus:outline-none focus:border-blue-500 transition-colors`}
                placeholder="Smart Home Automation System"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-400">{errors.title.message}</p>
              )}
            </div>

            {/* Project Details */}
            <div>
              <label className="block text-sm font-medium mb-2">Project Details *</label>
              <textarea
                {...register('details', {
                  required: 'Project details are required',
                  minLength: { value: 20, message: 'Please provide at least 20 characters' }
                })}
                className={`w-full p-3 h-32 rounded-lg bg-white/5 border ${
                  errors.details ? 'border-red-500' : 'border-white/10'
                } placeholder:text-white/50 focus:outline-none focus:border-blue-500 transition-colors resize-none`}
                placeholder="Describe your project requirements, goals, and any specific features you need..."
              />
              {errors.details && (
                <p className="mt-1 text-sm text-red-400">{errors.details.message}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.button
                type="button"
                onClick={sendWhatsApp}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 rounded-lg font-semibold transition-colors"
              >
                <span>📱</span>
                Send via WhatsApp
              </motion.button>
              
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isLoading
                    ? 'bg-blue-500/50 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Email
                  </>
                )}
              </motion.button>
            </div>

            {/* Success Message */}
            {isSubmitted && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-4 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400"
              >
                <CheckCircle size={20} />
                <span>Message sent successfully! We'll get back to you soon.</span>
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  )
}

export default Contact
